import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getDatabase, ref, onValue, off, set } from 'firebase/database';
import axios from 'axios';
import styled from 'styled-components';

import TaskModalContext from './TaskModal.Context';
import IconButton from '../../../../components/Icon-Button';
import UserAvatar from 'components/UserAvatar';

import { getFirebaseBackend } from '../../../../helpers/firebaseHelper';
import { formatDate, copyToClipBoard } from '../../../../utils';
import { GDriveIcon } from 'assets/icons';
import ModalContentContext from '../Modal-Content/ModalContent.Context';
import ModalContent from '../Modal-Content';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { useAuth } from 'hooks/useAuth';

const PRIORITY_CLASSES = {
  low: 'priority--low',
  normal: 'priority--medium',
  high: 'priority--high',
  urgent: 'priority--urgent',
};
const PRIORITY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const TaskModal = ({ accountId }) => {
  const firebaseHelper = getFirebaseBackend();
  const auth = useAuth();

  const { t: translate } = useTranslation();
  const { adminStatus, modal, setModal, id, data } = useContext(TaskModalContext);

  const [content, setContent] = useState(data);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderLink, setFolderLink] = useState(null);
  const implementationDateObj = content?.custom_fields?.find(
    (field) => field.name === 'Data de implementação',
  );
  const implementationDate = implementationDateObj?.value
    ? new Date(+implementationDateObj.value)
    : null;

  const onCardOpened = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('id', String(id));
    window.history.replaceState({ path: url.toString() }, document.title, url.toString());
    isMarkdownOverflown();
  };

  const onCardClosed = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('id');
    url.searchParams.delete('acc');
    url.searchParams.delete('expGraph');
    url.searchParams.delete('expConfig');
    window.history.replaceState({ path: url.toString() }, document.title, url.toString());
    setShowMoreButton(false);
    setContent(null);
    setFiles(null);
    setComments(null);
    setChecklist(null);
    setFolderLink(null);
  };

  const copyTask = (event, id) => {
    const url = new URL(window.location.href);
    url.searchParams.set('id', String(id));
    copyToClipBoard(event, url.toString());
  };

  const isMarkdownOverflown = async () => {
    const markdownElem = document.querySelector('.markdown-description');
    const fadeElem = document.querySelector('.markdown-description-fade');

    if (!markdownElem || !fadeElem) {
      setTimeout(isMarkdownOverflown, 100);
      return;
    }

    const imgTags = Array.from(markdownElem.querySelectorAll('img'));
    const isCached = imgTags.every((img) => img.complete);
    if (!isCached) {
      imgTags.forEach((img) => {
        if (img.complete) return;
        img.onload = () => {
          if (markdownElem.scrollHeight > markdownElem.clientHeight) {
            fadeElem.classList.add('show-more');
          } else {
            fadeElem.classList.remove('show-more');
          }
        };
      });
      return;
    }

    if (markdownElem.scrollHeight > markdownElem.clientHeight) {
      fadeElem.classList.add('show-more');
    } else {
      fadeElem.classList.remove('show-more');
    }
  };
  const createFolder = async (folderType) => {
    try {
      setIsCreatingFolder(true);
      const account = await firebaseHelper.getAccount(accountId);
      const accountName = account.name;

      if (!content?.name) {
        throw new Error('Task name is missing. Cannot create folder without a task name.');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_WEBHOOK_CREATE_FOLDER_TASK}`,
        {
          accountName: accountName,
          title: content.name,
          type: folderType,
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_GDOCS_FLOW_KEY}`,
          },
        },
      );

      const link = response.data.folderTask;
      setFolderLink(link);

      const db = getDatabase();
      const taskRef = ref(db, `task-drives-v1/${accountId}/${content.id}`);
      await set(taskRef, link);
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error(translate('Error while creating folder, try again later.'));
    } finally {
      setIsCreatingFolder(false);
    }
  };

  useEffect(async () => {
    if (!modal || !id) return;

    const auth = getAuth();
    const user = auth.currentUser;
    await user.getIdToken().then((token) => {
      axios
        .get(`${process.env.REACT_APP_API_URL}/task?taskId=${id}&accountId=${accountId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setContent(response.data.task);
        })
        .catch((error) => {
          console.error('Error fetching task details:', error);
          toast.error(translate('Error fetching task details. Please try again later.'));
        });

      axios
        .get(`${process.env.REACT_APP_API_URL}/attachments?taskId=${id}&accountId=${accountId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setFiles(response.data.files);
        });
    });

    const db = getDatabase();
    const commentsRef = ref(db, `task-comments-v1/${accountId}/${id}`);

    const handleCommentValueChange = async (snapshot) => {
      const data = snapshot.val();
      const commentsArr = Object.values(data || {});
      const nameById = {};
      for (const [index, comment] of Object.entries(commentsArr)) {
        if (nameById[comment.by]) {
          commentsArr[index] = {
            ...comment,
            byName: nameById[comment.by],
            self: comment.by === auth?.currentUser?.uid,
          };
          continue;
        }

        const username = await firebaseHelper.getUserName(comment.by);
        nameById[comment.by] = username || 'Unknown User';
        commentsArr[index] = {
          ...comment,
          byName: nameById[comment.by],
          self: comment.by === auth?.currentUser?.uid,
        };
      }
      setComments(commentsArr);
    };

    onValue(commentsRef, handleCommentValueChange);

    const drivesRef = ref(db, `task-drives-v1/${accountId}/${id}`);

    const handleDriveValueChange = async (snapshot) => {
      const data = snapshot.val();
      setFolderLink(data);
    };

    onValue(drivesRef, handleDriveValueChange);

    return () => {
      off(commentsRef, 'value', handleCommentValueChange);
      off(drivesRef, 'value', handleDriveValueChange);
    };
  }, [modal, id, accountId]);

  return (
    <Modal
      isOpen={modal}
      role="dialog"
      toggle={() => setModal(!modal)}
      size="xl"
      onClosed={onCardClosed}
      onOpened={onCardOpened}
      unmountOnClose={true}
      keyboard={true}
      autoFocus={true}
      centered={true}
      contentClassName="modal-task"
      tabIndex="-1"
    >
      <ModalHeader cssModule={{ 'modal-title': 'w-100 text-center mb-0' }}>
        <div
          className="modal--header ms-3"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <UncontrolledDropdown>
                <DropdownToggle
                  className="btn"
                  style={{
                    backgroundColor: 'rgba(116, 120, 141, 0.2)',
                    border: 'none',
                  }}
                  caret
                  disabled
                >
                  <span style={{ color: 'rgba(116, 120, 141, 1)', textTransform: 'capitalize' }}>
                    {content?.status?.status}
                  </span>
                  <i className="mdi mdi-chevron-down" style={{ color: 'rgba(116, 120, 141, 1)' }} />
                </DropdownToggle>
              </UncontrolledDropdown>
            </div>
            <div className="d-flex align-items-center gap-2">
              <UncontrolledDropdown>
                <DropdownToggle
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'default',
                  }}
                  caret
                >
                  <div className="d-flex justify-content-center align-items-center">
                    <div className="avatar-group">
                      {_.size(content?.assignees) === 0 ? (
                        <div className="avatar-group-item">
                          <div className="avatar-xs">
                            <UserAvatar userId={null} usePlaceholder={true} />
                          </div>
                        </div>
                      ) : (
                        _.map(content?.assignees, (member, index) => {
                          if (!(index < 4)) return;

                          return (
                            <div className="avatar-group-item" key={index}>
                              <div
                                alt={`${translate('Photo of')} ${member.username}`}
                                id={`card-${member.id}-modal-${content.id}`}
                              >
                                <img
                                  src={member?.profilePicture}
                                  alt={member?.username}
                                  className="rounded-circle avatar-xs"
                                />
                              </div>
                              <UncontrolledTooltip
                                defaultOpen={false}
                                flip
                                placement="right"
                                target={`card-${member.id}-modal-${content.id}`}
                              >
                                {member.username}
                              </UncontrolledTooltip>
                            </div>
                          );
                        })
                      )}
                      {_.size(content?.assignees) > 4 && (
                        <div className="avatar-group-item" id={`more-${content.id}`}>
                          <div className="avatar-xs">
                            <span
                              className="avatar-title rounded-circle text-white font-size-16"
                              style={{ backgroundColor: 'black' }}
                            >
                              {`${_.size(content.assignees) - 4}+`}
                            </span>
                          </div>
                          <UncontrolledTooltip
                            defaultOpen={false}
                            flip
                            placement="right"
                            target={`more-${content.id}`}
                          >
                            {content?.assignees
                              .slice(4)
                              .map((member) => member.username)
                              .join(', ')}
                          </UncontrolledTooltip>
                        </div>
                      )}
                    </div>
                    <i
                      className="mdi mdi-chevron-down ms-1"
                      style={{ color: 'rgba(116, 120, 141, 1)', fontSize: '1.5em' }}
                    />
                  </div>
                </DropdownToggle>
              </UncontrolledDropdown>
            </div>
            <div className="d-flex align-items-center gap-2">
              {content?.priority?.priority ? (
                <span
                  className={`${
                    PRIORITY_CLASSES[content?.priority?.priority]
                  } badge rounded-pill font-size-11`}
                  style={{ lineHeight: '2' }}
                >
                  {translate(PRIORITY_LABELS[content?.priority?.priority])}
                </span>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2">
              {implementationDate ? (
                <div className="d-flex flex-row align-items-center">
                  <i
                    className="bx bx-award"
                    style={{
                      color: '#484848',
                      fontSize: '1.5em',
                      marginRight: '.25em',
                    }}
                  />
                  <div>
                    <p
                      style={{
                        color: '#ADB5BD',
                        fontSize: '.7em',
                        marginBottom: '0',
                      }}
                    >
                      {translate('Implemented on') + ':'}
                    </p>
                    <p
                      style={{
                        color: '#484848',
                        fontSize: '.8em',
                        marginBottom: '0',
                      }}
                    >
                      {formatDate(implementationDate, translate('date-enUS'))}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '.5em' }}>
            {isCreatingFolder || folderLink ? (
              <ButtonDrive
                disabled={isCreatingFolder}
                outline={Boolean(folderLink)}
                className="d-flex justify-content-center align-items-center"
                onClick={() => window.open(folderLink, '_blank')}
              >
                {folderLink ? (
                  <div className="d-flex align-items-center gap-1">
                    <GDriveIcon />
                    {`${translate('Open in')} Drive`}
                  </div>
                ) : (
                  <div
                    className="spinner-border text-light"
                    role="status"
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      position: 'absolute',
                    }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </ButtonDrive>
            ) : (
              <UncontrolledDropdown>
                <StyledDropdownToggle
                  outline
                  color="secondary"
                  className="d-flex align-items-center gap-1"
                >
                  <GDriveIcon />
                  {translate('Create folder')}
                </StyledDropdownToggle>
                <div style={{ height: '0.38rem' }} />
                <DropdownMenu
                  style={{
                    border: '1px solid #E2E8F0',
                    width: '16.75rem',
                  }}
                >
                  <DropdownItem
                    className="px-3 py-2 mb-2"
                    onClick={() => createFolder('REPORTS')}
                    disabled={!content || !content?.name}
                  >
                    {translate('Reports')}
                  </DropdownItem>
                  <DropdownItem
                    className="px-3 py-2 mb-2"
                    onClick={() => createFolder('PLANNING_AND_STUDIES')}
                    disabled={!content || !content?.name}
                  >
                    {translate('Planning and Studies')}
                  </DropdownItem>
                  <DropdownItem
                    className="px-3 py-2"
                    onClick={() => createFolder('ANALYSES_AND_GUIDELINES')}
                    disabled={!content || !content?.name}
                  >
                    {translate('Analyses and Guidelines')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}

            <UncontrolledDropdown>
              <DropdownToggle
                style={{ border: 'none', display: 'flex', height: '100%' }}
                caret
                tag="div"
              >
                <IconButton
                  iconProps={{
                    type: 'regular',
                    name: 'dots-vertical-rounded',
                    color: '#495057',
                    size: '1.75em',
                  }}
                />
              </DropdownToggle>

              <DropdownMenu>
                <DropdownItem onClick={(event) => copyTask(event, content.id)}>
                  <i className="bx bx-copy" style={{ fontSize: '1.25em', color: 'black' }} />{' '}
                  <span style={{ fontSize: '1.1em', color: 'black' }}>{translate('Copy URL')}</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>

            <IconButton
              iconProps={{
                type: 'regular',
                name: 'x',
                color: '#495057',
                size: '2.2em',
              }}
              onClick={() => setModal(false)}
            />
          </div>
        </div>
      </ModalHeader>

      <ModalContentContext.Provider
        value={{
          content,
          setContent,
          adminStatus,
          checklist,
          comments,
          files,
          setFiles,
          showMoreButton,
          setShowMoreButton,
        }}
      >
        <ModalContent accountId={accountId} taskId={id} />
      </ModalContentContext.Provider>
    </Modal>
  );
};

const ButtonDrive = styled(Button)`
  color: #343a40;
  border-color: #e2e8f0;
  height: 2.75rem;
  min-width: 7.5rem;
  position: relative;

  &:hover {
    color: white;
  }
`;

const StyledDropdownToggle = styled(DropdownToggle)`
  color: #343a40;
  border-color: #e2e8f0;
  height: 2.75rem;
  width: 7.5rem;
  position: relative;

  &:hover {
    color: white;
  }
`;

TaskModal.propTypes = {
  accountId: PropTypes.string,
};

export default TaskModal;
